import React, { useEffect, useMemo, useState } from 'react';
import { FileText, Save, RotateCcw } from 'lucide-react';
import { fetchData } from '@/fetchapi/fetchapi';
import { toast } from 'sonner';

// READ OPTIONS — how the engine reads a delimited text file (csv/txt/tsv/dat/psv).
//
// The engine's normalizer (panda.normalize_delimited, called from the file
// readers on core) detects encoding, delimiter, header line, preamble, repeated
// page headers, footer, trailing-minus negatives and thousands separators on
// its own and records what it found on the source as
// config.read_options_detected. A person can PIN any option in
// config.read_options — explicit values override detection — and re-run the
// analysis / pipeline. Nothing here is file-specific: it is one generic
// contract for every report dump, mainframe extract and odd export.
//
// Persistence: PUT /api/storage_config/data_source/:id { config } (the same
// route the connection editors use), merging into the existing config so
// nothing else on the source is lost.

const KNOWN = [
  ['encoding', 'Encoding', 'utf-8 · utf-8-sig · cp1252 · latin-1'],
  ['delimiter', 'Delimiter', ', | ; tab (\\t) ~ ^'],
  ['quotechar', 'Quote char', '"'],
  ['header', 'Header line (0-based)', 'index of the column-header line'],
  ['skip_rows', 'Skip rows', 'lines before the header (preamble)'],
  ['skip_footer', 'Skip footer', 'trailing lines to drop'],
  ['comment_prefix', 'Comment prefix', 'lines starting with this are dropped'],
  ['null_values', 'Null values', 'comma-separated tokens read as null'],
  ['drop_repeated_header', 'Drop repeated headers', 'true / false'],
  ['drop_malformed', 'Drop malformed lines', 'true / false'],
  ['strip_cells', 'Strip cells', 'true / false'],
  ['trailing_minus', 'Trailing minus (256.62-)', 'true / false'],
  ['thousands_separator', 'Thousands separator', ', or . or blank'],
];

const isFileKind = (kind) => ['csv', 'file', 'csv_upload', 'txt', 'tsv', 'dat', 'psv'].includes(String(kind || '').toLowerCase());

const fmt = (v) => {
  if (v === null || v === undefined) return '—';
  if (v === '\t') return 'tab';
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (Array.isArray(v)) return v.join(', ');
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

const toEditable = (opts) => JSON.stringify(opts && Object.keys(opts).length ? opts : {}, null, 2);

export default function ReadOptionsCard({ dataSource, onSaved }) {
  const loaded = useMemo(() => {
    let c = dataSource?.config;
    if (typeof c === 'string') { try { c = JSON.parse(c); } catch { c = {}; } }
    return c || {};
  }, [dataSource]);
  // What THIS card saved last. The recipe write commits to Dolt after the
  // response is sent, so a reload fired right after the PUT can still read the
  // previous config; the saved value stays authoritative here until a reload
  // agrees with it.
  const [savedConfig, setSavedConfig] = useState(null);
  useEffect(() => {
    if (savedConfig && JSON.stringify(loaded.read_options || null) === JSON.stringify(savedConfig.read_options || null)) setSavedConfig(null);
  }, [loaded, savedConfig]);
  const config = savedConfig || loaded;
  const detected = config.read_options_detected || null;
  const pinned = config.read_options || null;
  const [text, setText] = useState(toEditable(pinned));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { setText(toEditable(pinned)); setErr(''); }, [pinned]);

  if (!dataSource || !isFileKind(dataSource.kind)) return null;

  const parsed = (() => { try { return JSON.parse(text || '{}'); } catch (e) { return e; } })();
  const invalid = parsed instanceof Error;
  const dirty = text.trim() !== toEditable(pinned).trim();

  const save = async (nextOptions) => {
    if (nextOptions instanceof Error) { setErr('Options must be a JSON object, e.g. {"delimiter": "|", "skip_rows": 3}'); return; }
    if (nextOptions && (typeof nextOptions !== 'object' || Array.isArray(nextOptions))) { setErr('Options must be a JSON object'); return; }
    setSaving(true); setErr('');
    try {
      const next = { ...config };
      if (nextOptions && Object.keys(nextOptions).length) next.read_options = nextOptions; else delete next.read_options;
      await fetchData(`storage_config/data_source/${dataSource.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ config: next }),
      });
      setSavedConfig(next);
      toast.success(next.read_options ? 'Read options saved — regenerate the analysis or run the pipeline to apply them.' : 'Read options cleared — detection is back in charge.');
      setTimeout(() => onSaved?.(), 400);
    } catch (e) {
      setErr(e?.message || 'Could not save read options');
    } finally { setSaving(false); }
  };

  // Scalar facts of the last read (counts, delimiter, encoding…). Nested
  // objects — the EFFECTIVE options the normaliser ended up using — are shown
  // folded below the list, never inline: one long JSON value used to widen the
  // grid past the card and push every row out of view.
  const detectedEntries = detected ? Object.entries(detected) : [];
  const detectedRows = detectedEntries.filter(([k, v]) => k !== 'total_lines' && (v === null || typeof v !== 'object'));
  const detectedNested = detectedEntries.filter(([, v]) => v && typeof v === 'object');

  return (
    <div className="bg-card-bg rounded-[8px] p-4 md:p-5" data-testid="read-options-card">
      <div className="flex items-center gap-2 mb-1">
        <FileText size={18} color="#377AD6" className="shrink-0" />
        <h2 className="text-base font-medium" style={{ color: 'var(--page-text)' }}>Read options</h2>
        {pinned && (
          <span className="inline-flex items-center h-[18px] rounded-[9px] px-2 text-xs font-medium" data-testid="read-options-pinned-badge"
            style={{ background: 'rgba(233,171,48,0.18)', color: 'rgba(180,120,0,0.95)' }}>pinned</span>
        )}
      </div>
      <p className="text-sm mb-4" style={{ color: 'var(--page-muted)' }}>
        How the file is read: delimiter, encoding, header line, preamble and footer, repeated page headers,
        trailing-minus negatives and thousands separators are detected on every read. Pin a value below to override detection.
      </p>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <div className="text-xs font-medium uppercase tracking-wide mb-2" style={{ color: 'var(--page-muted)' }}>Detected on the last read</div>
          {detectedRows.length === 0 ? (
            <div className="text-sm" style={{ color: 'var(--page-muted)' }} data-testid="read-options-detected-empty">
              Nothing detected yet — generate the analysis statistics (or run the pipeline) and the engine records what it found here.
            </div>
          ) : (
            <>
              <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1 text-sm" data-testid="read-options-detected">
                {detectedRows.map(([k, v]) => (
                  <React.Fragment key={k}>
                    <dt className="whitespace-nowrap" style={{ color: 'var(--page-muted)' }}>{k.replace(/_/g, ' ')}</dt>
                    <dd className="font-mono min-w-0 break-all" style={{ color: 'var(--page-text)' }} data-testid={`read-options-detected-${k}`}>{fmt(v)}</dd>
                  </React.Fragment>
                ))}
              </dl>
              {detectedNested.map(([k, v]) => (
                <details key={k} className="mt-2 text-xs" style={{ color: 'var(--page-muted)' }} data-testid={`read-options-detected-nested-${k}`}>
                  <summary className="cursor-pointer">{k === 'read_options' ? 'Effective options used for that read' : k.replace(/_/g, ' ')}</summary>
                  <pre className="mt-1 p-2 rounded-[6px] font-mono whitespace-pre-wrap break-all" style={{ background: 'var(--table-stripe)', color: 'var(--page-text)' }}>{JSON.stringify(v, null, 2)}</pre>
                </details>
              ))}
            </>
          )}
        </div>
        <div>
          <div className="text-xs font-medium uppercase tracking-wide mb-2" style={{ color: 'var(--page-muted)' }}>Pinned options (JSON)</div>
          <textarea
            className="w-full min-h-[140px] rounded-[6px] p-2 text-sm font-mono"
            style={{ background: 'var(--input-bg, var(--card-bg))', color: 'var(--page-text)', border: `1px solid ${invalid ? 'rgba(220,38,38,0.7)' : 'var(--input-border)'}` }}
            value={text}
            onChange={(e) => setText(e.target.value)}
            spellCheck={false}
            data-testid="read-options-editor"
            placeholder='{"delimiter": "|", "skip_rows": 3, "trailing_minus": true}'
          />
          <details className="mt-1 text-xs" style={{ color: 'var(--page-muted)' }}>
            <summary className="cursor-pointer">Known options</summary>
            <ul className="mt-1 grid gap-0.5">
              {KNOWN.map(([k, label, hint]) => (
                <li key={k}><span className="font-mono">{k}</span> — {label} <span className="opacity-70">({hint})</span></li>
              ))}
            </ul>
          </details>
          {err && <div className="text-sm text-red-600 mt-1" data-testid="read-options-error">{err}</div>}
          <div className="flex flex-wrap gap-2 mt-3">
            <button
              type="button"
              className="flex items-center gap-2 h-8 px-3 rounded-[5px] text-white text-sm font-medium cursor-pointer border-0 disabled:opacity-50 disabled:cursor-not-allowed bg-nav-active hover:bg-nav-active-dark transition-colors"
              disabled={saving || invalid || !dirty}
              onClick={() => save(parsed)}
              data-testid="read-options-save"
            >
              <Save size={16} strokeWidth={2.5} />
              {saving ? 'Saving…' : 'Save read options'}
            </button>
            {pinned && (
              <button
                type="button"
                className="flex items-center gap-2 h-8 px-3 rounded-[5px] border-2 border-nav-active text-nav-active bg-card-bg text-sm font-medium cursor-pointer hover:border-nav-active-hover hover:text-nav-active-hover transition-colors disabled:opacity-50"
                disabled={saving}
                onClick={() => save(null)}
                data-testid="read-options-clear"
              >
                <RotateCcw size={16} strokeWidth={2.5} />
                Back to detection
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
